import { FIELD_KIND, KeyPair } from '@wizeoo/akm-compute-lib';
import { ChartparameterObj } from '../../components/wiz-ngx-charts/config/chart-parameter.obj';
import { colorSets } from '../../components/ngx-charts/utils/color-sets';
import { CHART_WIDGET_MODE } from '../../model/enums';
import { ChartDecriptionObj } from '../../components/wiz-ngx-charts/config/chart-description.obj';
import { FakeVO } from '../../vo/common/tool/fake.vo';
import { ChartItemObj } from '../../components/wiz-ngx-charts/config/chart-item.obj';


export interface ChartBuilderItem {
  masterKey: string; // le code#level du chrono / FD porteur de l'axe
  key: string; // la clé de codif de l'élément d'axe
  sortKey: any; // la clé de tri de cet élément
  label: string; // le libellé pour l'élément d'axe
  active: boolean; // à minima une valeur sur une des séries
  itemMap: Map<string, ChartItemObj>; // les item par serie.name
}

/**
 * gestion des charts
 */
export class ChartsEngine {
  /** nb items par défaut pour un chart */
  public static chartMaxItems: number = 50;

  /** type de chart par défault */
  public static defaultChartType: string = CHART_WIDGET_MODE.BAR;

  /** typologie des champs de configuration des charts */
  private static _chartParameterList: ChartparameterObj[] = null;
  public static get chartParameterList(): ChartparameterObj[] {
    if (!this._chartParameterList) {
      const result: ChartparameterObj[] = [];
      result.push(new ChartparameterObj('legend', FIELD_KIND.BOOLEAN, false));
      result.push(new ChartparameterObj('legendTitle', FIELD_KIND.STRING, null, 'legend'));
      result.push(
        new ChartparameterObj(
          'legendPosition',
          FIELD_KIND.STRING,
          'right',
          'legend',
          ChartsEngine.chartLegendPositionList,
          'code'
        )
      );
      result.push(new ChartparameterObj('scheme', FIELD_KIND.STRING, colorSets[0].name, null, colorSets, 'name'));
      result.push(new ChartparameterObj('color', FIELD_KIND.COLOR));

      result.push(new ChartparameterObj('tooltipDisabled', FIELD_KIND.BOOLEAN, false));
      result.push(new ChartparameterObj('labels', FIELD_KIND.BOOLEAN, false)); // affichage des libellés sur PieChart

      result.push(new ChartparameterObj('xAxis', FIELD_KIND.BOOLEAN, true)); // axe des X visible
      result.push(new ChartparameterObj('showXAxisLabel', FIELD_KIND.BOOLEAN, true, 'xAxis')); // libellé axe des X visible
      result.push(new ChartparameterObj('xAxisLabel', FIELD_KIND.STRING, null, 'showXAxisLabel'));

      result.push(new ChartparameterObj('yAxis', FIELD_KIND.BOOLEAN, true)); // axe des Y visible
      result.push(new ChartparameterObj('showYAxisLabel', FIELD_KIND.BOOLEAN, true, 'yAxis')); // libellé axe des Y visible
      result.push(new ChartparameterObj('yAxisLabel', FIELD_KIND.STRING, null, 'showYAxisLabel'));

      result.push(new ChartparameterObj('showDataLabel', FIELD_KIND.BOOLEAN, false)); // affichage des libellés sur bars

      result.push(new ChartparameterObj('autoScale', FIELD_KIND.BOOLEAN, true)); // échelle auto

      result.push(new ChartparameterObj('yScaleMax', FIELD_KIND.NUMBER, null));
      result.push(new ChartparameterObj('yScaleMin', FIELD_KIND.NUMBER, null));

      result.push(new ChartparameterObj('xScaleMax', FIELD_KIND.NUMBER, null));
      result.push(new ChartparameterObj('xScaleMin', FIELD_KIND.NUMBER, null));

      result.push(new ChartparameterObj('trimXAxisTicks', FIELD_KIND.BOOLEAN, true)); // tronquer les libellés X
      result.push(new ChartparameterObj('maxXAxisTickLength', FIELD_KIND.NUMBER, 15, 'trimXAxisTicks'));

      result.push(new ChartparameterObj('trimYAxisTicks', FIELD_KIND.BOOLEAN, true)); // tronquer les libellés Y
      result.push(new ChartparameterObj('maxYAxisTickLength', FIELD_KIND.NUMBER, 15, 'trimYAxisTicks'));

      result.push(new ChartparameterObj('barPadding', FIELD_KIND.NUMBER, 5, null, null, null, 0, 100));

      result.push(new ChartparameterObj('timeline', FIELD_KIND.BOOLEAN, false)); // X temporel

      result.push(new ChartparameterObj('min', FIELD_KIND.NUMBER, null)); // valeur min sur une jauge
      result.push(new ChartparameterObj('max', FIELD_KIND.NUMBER, null)); // valeur max sur une jauge
      result.push(new ChartparameterObj('angleSpan', FIELD_KIND.NUMBER, 290, null, null, null, 90, 360)); // taille de l'angle pour une jauge

      result.push(new ChartparameterObj('showAxis', FIELD_KIND.BOOLEAN, true)); // affichage de l'échelle sur une jauge
      result.push(new ChartparameterObj('bigSegments', FIELD_KIND.NUMBER, 10, 'showAxis')); // nb repère sur l'axe d'une jauge
      result.push(new ChartparameterObj('showText', FIELD_KIND.BOOLEAN, false)); // affichage du libellé pour une jauge
      result.push(new ChartparameterObj('units', FIELD_KIND.STRING, null, 'showText')); // libellé pour une jauge

      this._chartParameterList = result;
    }
    return this._chartParameterList;
  }

  /**
   * Liste des charts disponible avec le compo wiz-ngx-charts
   * 'Bar' | 'BarVGrouped' | 'BarVStacked' | 'Bubble' | 'Gauge' | 'Line' | 'NumberCard' | 'Pie' | 'PieGrid' | 'PieAdvanced' | 'Polar' | 'Treemap';
   * la propriété simple est un flag indiquant si le chart utilise des données simple ou en serie
   */
  public static chartType: ChartDecriptionObj[] = [
    new ChartDecriptionObj(CHART_WIDGET_MODE.PIE, 1, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'labels',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.BAR, 1, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'showDataLabel',
      'barPadding',
      'yScaleMax',
      'yScaleMin',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.BAR_H, 1, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'showDataLabel',
      'barPadding',
      'xScaleMax',
      'xScaleMin',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.GAUGE, 1, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'min',
      'max',
      'units',
      'bigSegments',
      'showAxis',
      'angleSpan',
      'showText',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.CARD, 1, 0, ['scheme', 'legend', 'legendTitle', 'color']),
    new ChartDecriptionObj(CHART_WIDGET_MODE.TREEMAP, 1, 0, ['scheme', 'tooltipDisabled']),

    new ChartDecriptionObj(CHART_WIDGET_MODE.BAR_V_STACKED, 0, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'showDataLabel',
      'barPadding',
      'yScaleMax',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.BAR_V_GROUPED, 0, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'showDataLabel',
      'barPadding',
      'yScaleMax',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.BAR_H_STACKED, 0, 0, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'showDataLabel',
      'barPadding',
      'xScaleMax',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.LINE, 1, 1, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'xScaleMax',
      'xScaleMin',
      'yScaleMax',
      'yScaleMin',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
      'autoScale',
      'timeline',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.MULTI_LINE, 0, 1, [
      'legend',
      'scheme',
      'legendTitle',
      'legendPosition',
      'tooltipDisabled',
      'xAxis',
      'yAxis',
      'showXAxisLabel',
      'showYAxisLabel',
      'xAxisLabel',
      'yAxisLabel',
      'xScaleMax',
      'xScaleMin',
      'yScaleMax',
      'yScaleMin',
      'trimXAxisTicks',
      'maxXAxisTickLength',
      'trimYAxisTicks',
      'maxYAxisTickLength',
      'autoScale',
      'timeline',
    ]),
    new ChartDecriptionObj(CHART_WIDGET_MODE.BUBBLE, 1, 2, []),
  ];

  /** positions de légende */
  private static _chartLegendPositionList: FakeVO[] = null;
  public static get chartLegendPositionList(): FakeVO[] {
    if (!this._chartLegendPositionList) {
      const result: FakeVO[] = [];
      result.push(new FakeVO('right', '$.chart.legend.position.right.$', 1));
      result.push(new FakeVO('below', '$.chart.legend.position.below.$', 2));
      this._chartLegendPositionList = result;
    }
    return this._chartLegendPositionList;
  }
}
